import React, {forwardRef, useLayoutEffect, useRef, useState} from 'react'
import InputMask from 'react-input-mask'
import {UtilUi} from '@common/utils-ui'
import {updateRef} from '@containers/element-utils'
import {
  TwIcon,
  TwIconButton,
  TwInput,
  TwInputContainer,
  TwInputWrapper,
  TwLabel,
  TwRightLabel,
  TwSupport
} from './containers'
import {InputNewProps} from './types'

export const InputNew = forwardRef<HTMLInputElement, InputNewProps>(
  (props, forwardedRef) => {
    const {
      type = 'text' as const,
      value,
      onChange,
      mask,
      maskPlaceholder = '•',
      label,
      rightLabel,
      support,
      placeholder,
      cursorView,
      onKeyDown,
      onFocus,
      onBlur
    } = props
    const {
      iconRight,
      iconLeftError,
      iconRightError,
      size = 'md',
      error,
      disabled,
      leftIconDisabled,
      className,
      inputClassName,
      baseFocusStyle,
      isAutoWidth,
      textInputStyle,
      rightIconClassName
    } = props
    const {tabIndex, autoFocus, step, min, max} = props
    const ref = useRef<HTMLInputElement>()
    const [inputWidth, setInputWidth] = useState<number | null>(null)

    const iconLeft = props.icon ?? props.iconLeft

    const onIconLeftClick = props.onIconClick ?? props.onIconLeftClick
    const onIconLeftMouseDown =
      props.onIconMouseDown ?? props.onIconLeftMouseDown
    const onIconLeftMouseUp = props.onIconMouseUp ?? props.onIconLeftMouseUp
    const onIconRightClick = props.onIconClick ?? props.onIconRightClick
    const onIconRightMouseDown =
      props.onIconMouseDown ?? props.onIconRightMouseDown
    const onIconRightMouseUp = props.onIconMouseUp ?? props.onIconRightMouseUp

    useLayoutEffect(() => {
      if (isAutoWidth) {
        const {width} = UtilUi.calcTextBounds(value, textInputStyle)
        setInputWidth(width)
      }
    }, [value, textInputStyle, isAutoWidth])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      onChange?.(mask ? value.replace(maskPlaceholder, '') : value)
    }

    const commonProps = {
      ref: (el: HTMLInputElement) => {
        ref.current = el
        updateRef(el, forwardedRef)
      },
      className: inputClassName,
      $cursorView: cursorView,
      $error: error,
      $iconLeft: iconLeft,
      $iconRight: iconRight,
      $disabled: disabled,
      value: value,
      onChange: handleChange,
      onFocus: onFocus,
      onBlur: onBlur,
      onKeyDown: onKeyDown,
      placeholder: placeholder,
      disabled: disabled,
      tabIndex: tabIndex,
      autoFocus: autoFocus,
      step: step,
      min: min,
      max: max,
      $size: size,
      $baseFocusStyle: baseFocusStyle,
      $type: type,
      $isAutoWidth: isAutoWidth,
      style: isAutoWidth && inputWidth ? {width: `${inputWidth}px`} : undefined
    }

    return (
      <TwInputContainer
        className={className}
        onClick={e => {
          e.stopPropagation()
          ref.current?.focus()
        }}
      >
        {label && (
          <TwLabel $disabled={disabled}>
            {label}
            {rightLabel && <TwRightLabel>{rightLabel}</TwRightLabel>}
          </TwLabel>
        )}
        <TwInputWrapper>
          {iconLeft &&
            (onIconLeftClick || onIconLeftMouseDown || onIconLeftMouseUp ? (
              <TwIconButton
                icon={iconLeft}
                onClick={onIconLeftClick}
                onMouseDown={onIconLeftMouseDown}
                onMouseUp={onIconLeftMouseUp}
                error={iconLeftError}
                $error={iconLeftError}
                $left
                $size={size}
                $disabled={disabled || leftIconDisabled}
                disabled={disabled}
              />
            ) : (
              <TwIcon
                icon={iconLeft}
                viewBox="0 0 24 24"
                $error={iconLeftError}
                $left
                $size={size}
                $disabled={disabled || leftIconDisabled}
              />
            ))}
          {mask ? (
            <InputMask
              mask={mask}
              maskPlaceholder={maskPlaceholder}
              value={value}
              onChange={handleChange}
            >
              <TwInput {...commonProps} />
            </InputMask>
          ) : (
            <TwInput {...commonProps} />
          )}
          {iconRight &&
            (onIconRightClick || onIconRightMouseDown || onIconRightMouseUp ? (
              <TwIconButton
                icon={iconRight}
                onClick={onIconRightClick}
                onMouseDown={onIconRightMouseDown}
                onMouseUp={onIconRightMouseUp}
                className={rightIconClassName}
                error={iconRightError}
                $error={iconRightError}
                $right
                $size={size}
                $disabled={disabled}
                disabled={disabled}
              />
            ) : (
              <TwIcon
                icon={iconRight}
                className={rightIconClassName}
                viewBox="0 0 24 24"
                $error={iconRightError}
                $right
                $size={size}
                $disabled={disabled}
              />
            ))}
        </TwInputWrapper>
        {support && <TwSupport $disabled={disabled}>{support}</TwSupport>}
      </TwInputContainer>
    )
  }
)
